import {Component, computed, effect, Input, signal} from '@angular/core';
import {ReportingService} from "../../../services/reporting.service";
import {IComprehensiveReport} from "../../../interfaces/reporting/IComprehensiveReport";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {IDateRangeReportingData} from "../../../interfaces/reporting/IDateRangeReportingData";
import {Content, TDocumentDefinitions} from "pdfmake/interfaces";
import * as pdfMake from "pdfmake/build/pdfmake";
import {ICharityContributionsBreakdown} from "../../../interfaces/ICharityContributionsBreakdown";
import {PdfUtilityService} from "../../../services/pdf-utility.service";
import {DatePipe} from "@angular/common";
import {IPdfSorting} from "../../../interfaces/reporting/IPdfSorting";
import {
  ComponentLevelErrorMessageComponent
} from "../../display-errors/component-level-error-message/component-level-error-message.component";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {MatButton, MatIconButton} from "@angular/material/button";
import {PipesModule} from "../../../pipes/pipes.module";
import {AgGridAngular} from "ag-grid-angular";
import {UtilService} from "../../../services/util.service";
import {FieldHelpIconComponent} from "../../../shared/field-help-icon/field-help-icon.component";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatIconModule} from "@angular/material/icon";
import {MatTooltipModule} from "@angular/material/tooltip";
import {ReportFieldDefinitionsService} from "../../../services/report-field-definitions.service";
import {MatDialog} from "@angular/material/dialog";
import {ReportGlossaryComponent} from "../../dialogs/report-glossary/report-glossary.component";
import {createGroupBreakdownColumnDefs} from "../../../shared/report-constants";
import {RegionTypes} from "../../../enum/RegionTypes";

@Component({
  selector: 'app-comprehensive-report-by-date-range',
  standalone: true,
  templateUrl: './comprehensive-report-by-date-range.component.html',
  imports: [
    ComponentLevelErrorMessageComponent,
    LoadingCardContentComponent,
    DatePipe,
    MatButton,
    MatIconButton,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PipesModule,
    AgGridAngular,
    FieldHelpIconComponent
  ],
  styleUrls: ['../../../pages/reports/reports.scss', 'comprehensive-report-by-date-range.component.scss']
})
export class ComprehensiveReportByDateRangeComponent {

  // ==========================================================================
  // Help Toggle System (shared pattern with other report components)
  // ==========================================================================
  showHelp = signal<boolean>(false);
  readonly fieldDescriptions: Record<string, string>;

  // ==========================================================================
  // Data Input
  // ==========================================================================
  @Input() set dateRangesSetter(valueP: IDateRangeReportingData) {
    this.dateRanges.set(valueP);
    this.reportingService.getComprehensiveGameReportData(
      valueP.Game.Id,
      valueP.StartDate,
      valueP.EndDate
    ).subscribe({
      next: (comprehensiveReportResP) => {
        this.comprehensiveReport = comprehensiveReportResP;
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.errorMessage = 'There was an issue retrieving your retention report. ' +
          'Please try refreshing the page or reaching out to a system administrator.';
        this.uiState = UIStateEnum.ShowData;
      }
    })
  }

  // ==========================================================================
  // Grid Configuration (uses shared column definitions)
  // ==========================================================================
  public groupBreakdownReportDefs: ColDef[];
  public columnApi!: GridApi;

  // ==========================================================================
  // UI State
  // ==========================================================================
  public uiState = UIStateEnum.ShowLoading;
  public uiStateForTemplate = UIStateEnum;
  public errorMessage = '';
  public comprehensiveReport!: IComprehensiveReport;
  public dateRanges = signal<IDateRangeReportingData | null>(null);

  pstLabel = computed(() => this.dateRanges()?.Game?.Region === RegionTypes.Quebec ? 'QST' : 'PST');

  constructor(private reportingService: ReportingService,
              private currencyPipe: FormatCurrencyPipe,
              private utilService: UtilService,
              private pdfUtilityService: PdfUtilityService,
              private datePipe: DatePipe,
              private fieldDefinitionsService: ReportFieldDefinitionsService,
              private matDialog: MatDialog) {
    // Initialize help toggle from localStorage
    this.showHelp.set(this.fieldDefinitionsService.getShowHelpPreference());

    // Pre-compute descriptions map once
    this.fieldDescriptions = this.fieldDefinitionsService.getDescriptionsMap();

    // Auto-persist showHelp changes to localStorage
    effect(() => {
      this.fieldDefinitionsService.setShowHelpPreference(this.showHelp());
    });

    // Use shared column definitions
    this.groupBreakdownReportDefs = createGroupBreakdownColumnDefs(this.currencyPipe);
  }

  // ==========================================================================
  // Shared Methods - Help & Glossary
  // ==========================================================================
  openGlossary(): void {
    this.matDialog.open(ReportGlossaryComponent, {
      width: '800px',
      maxHeight: '80vh'
    });
  }

  // ==========================================================================
  // Grid Methods
  // ==========================================================================
  onGridReady(params: GridReadyEvent<ICharityContributionsBreakdown>): void {
    this.columnApi = params.api;
  }

  private determineSortForPDF(): IPdfSorting | null {
    const sortedColumn = this.columnApi.getColumnState().find((col) => col.sort !== null);
    if (sortedColumn) {
      return {
        columnToSort: sortedColumn.colId,
        direction: sortedColumn.sort
      }
    }
    return null;
  }

  private sortColsForPdf(charityBreakdownP: ICharityContributionsBreakdown[], sortP: IPdfSorting): ICharityContributionsBreakdown[] {
    return charityBreakdownP.sort((a, b) => {
      // @ts-ignore
      const propA = a[sortP.columnToSort];
      // @ts-ignore
      const propB = b[sortP.columnToSort];

      if (sortP.direction === "asc") {
        return propA - propB;
      } else if (sortP.direction === "desc") {
        return propB - propA;
      } else {
        return 0;
      }
    });
  }

  // ==========================================================================
  // Export Methods
  // ==========================================================================
  public downloadAsCSV(): void {
    if (!this.comprehensiveReport) return;

    const filename = `${this.dateRanges()?.Game.ShortName}-comprehensive-report-${this.datePipe.transform(this.dateRanges()?.StartDate, 'yyyy-MM-dd')}-${this.datePipe.transform(this.dateRanges()?.EndDate, 'yyyy-MM-dd')}.csv`;

    const lines: string[] = [];

    // Every field goes through csvRow — formatted money carries a thousands separator above $999.99,
    // and an unquoted one splits the value across two columns.
    const row = (...valuesP: unknown[]) => lines.push(this.utilService.csvRow(valuesP));

    // Report title
    lines.push('Comprehensive Game Report');
    row(`Date Range: ${this.datePipe.transform(this.dateRanges()?.StartDate, 'MMM d y')} - ${this.datePipe.transform(this.dateRanges()?.EndDate, 'MMM d y')}`);
    lines.push('');

    // Section 1: Summary data as label-value pairs
    const ft = this.comprehensiveReport.FinancialTotals;
    row('Gross Sales', this.currencyPipe.transform(ft.GrossTotalSales));
    row('Dispute Fees', this.currencyPipe.transform(ft.StripeDisputeFees));
    row('Charity Host Admin Fees', this.currencyPipe.transform(ft.AdministrativeCommissionTotal));
    row('Causable Fees', this.currencyPipe.transform(ft.CausableFees));
    // Add tax breakdown based on what's available
    if (ft.GstTotal && ft.GstTotal > 0) {
      row('GST On Causable Fees', this.currencyPipe.transform(ft.GstTotal));
    }
    if (ft.PstTotal && ft.PstTotal > 0) {
      row(`${this.pstLabel()} On Causable Fees`, this.currencyPipe.transform(ft.PstTotal));
    }
    if (ft.HstTotal && ft.HstTotal > 0) {
      row('HST On Causable Fees', this.currencyPipe.transform(ft.HstTotal));
    }
    row('Stripe Fees', this.currencyPipe.transform(ft.StripeFees));
    row('Charity Refund Fees', this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.CharityRefundContributionTotal));
    row('Charity Dispute Fees', this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.CharityDisputeContributionTotal));
    row('Prize Amount', this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.NetPotContributionTotal));
    row('Net Proceeds For Charity', this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.NetCharityContribution));
    row('Draws With No Winner', this.comprehensiveReport.GameWinnerData.NumberOfDrawsWithNoWinners);
    row('Draws With Winner', this.comprehensiveReport.GameWinnerData.NumberOfDrawsWithWinners);
    row('Total Number Of Draws', this.comprehensiveReport.GameWinnerData.TotalNumberOfDraws);
    row('Total Numbers Played', this.comprehensiveReport.TicketCounts.NumberOfTicketsPlayed);
    row('Total Unplayed Numbers', this.comprehensiveReport.TicketCounts.NumberOfTicketsNotPlayed);
    row('Players Played', this.comprehensiveReport.UserCounts.NumberOfActiveUsers);
    row('Players Not Played', this.comprehensiveReport.UserCounts.NumberOfInactiveUsers);

    // Blank line separator
    lines.push('');

    // Section 2: Group/Charity breakdown table
    row('Group', 'Gross', 'Refund Fees', 'Dispute Fees', 'Net', 'Number of Players');

    if (this.comprehensiveReport.CharityBreakdown && this.comprehensiveReport.CharityBreakdown.length > 0) {
      this.comprehensiveReport.CharityBreakdown.forEach((charity: any) => {
        const gross = charity.CharityContribution ?? 0;
        const refundFees = charity.RefundAmount ?? 0;
        const disputeFees = charity.DisputeAmount ?? 0;
        const net = gross - refundFees - disputeFees;
        row(
          charity.Name,
          this.currencyPipe.transform(gross),
          this.currencyPipe.transform(refundFees),
          this.currencyPipe.transform(disputeFees),
          this.currencyPipe.transform(net),
          charity.TotalNumberOfPlayers
        );
      });
    }

    const csvContent = lines.join('\n');
    this.utilService.triggerDownload(csvContent, filename);
  }

  // ==========================================================================
  // PDF Generation
  // ==========================================================================
  generatePDF(): void {
    const contentP: Content[] = [];
    if (this.comprehensiveReport) {

      contentP.push({
        text: `Date Range: ${this.datePipe.transform(this.dateRanges()?.StartDate, 'yyyy-MM-dd')} - ${this.datePipe.transform(this.dateRanges()?.EndDate, 'yyyy-MM-dd')}`,
        style: this.pdfUtilityService.PdfStyles.pageTitle,
        margin: [0, 0, 0, 8]
      });

      contentP.push(this.getFinancialDetails());
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getGameCounts());
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getNumbers());
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getPlayers());
      contentP.push(this.pdfUtilityService.AddLineBreak());
      contentP.push(this.getCharityDetails());
      contentP.push(this.pdfUtilityService.AddLineBreak());

      const docDef: TDocumentDefinitions = {
        pageMargins: [40, 40, 40, 40],
        footer: (currentPage, pageCount) => {
          return {
            text: 'Page ' + currentPage.toString() + ' of ' + pageCount,
            style: this.pdfUtilityService.PdfStyles.footer
          };
        },
        content: contentP
      };

      const pdf = pdfMake.createPdf(docDef);
      pdf.open();
    }
  }

  private getCharityDetails(): Content[] {
    const charityDetails: Content[] = [];
    let charityBreakdown = this.comprehensiveReport?.CharityBreakdown;
    const getAnyColSorts = this.determineSortForPDF();
    if (getAnyColSorts && charityBreakdown) {
      charityBreakdown = this.sortColsForPdf(charityBreakdown, getAnyColSorts)
    }

    charityDetails.push(this.pdfUtilityService.generateSectionTitle('Group Breakdown'));
    charityDetails.push(this.pdfUtilityService.generateLabelValueRow('Total Group Contributions: ', `${this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.NetCharityContribution)}`));

    const charityBreakdownContent: Content = {
      layout: 'lightHorizontalLines',
      table: {
        headerRows: 1,
        body: [
          [
            {text: 'Group', fontSize: 10},
            {text: 'Total Amount', fontSize: 10},
            {text: 'Number of Players', fontSize: 10}
          ]
        ]
      },
      margin: [0, 16, 0, 16]
    };

    if (charityBreakdown && charityBreakdown?.length > 0) {
      charityBreakdown.forEach((contributionP) => {
        charityBreakdownContent.table.body.push([
          contributionP.Name, `${this.currencyPipe.transform(contributionP.CharityContribution)}`, contributionP.TotalNumberOfPlayers
        ]);
      });
    }

    charityDetails.push(charityBreakdownContent);
    return charityDetails;
  }

  private getNumbers(): Content[] {
    const playersAndNumbers: Content[] = [];
    playersAndNumbers.push(this.pdfUtilityService.generateSectionTitle('Player Numbers'));
    const columnOne = [
      this.pdfUtilityService.generateLabelValueRow('Total numbers played: ', `${this.comprehensiveReport.TicketCounts.NumberOfTicketsPlayed}`),
      this.pdfUtilityService.generateLabelValueRow('Total unplayed numbers: ', `${this.comprehensiveReport.TicketCounts.NumberOfTicketsNotPlayed}`)
    ];

    const gameColumns: Content = {
      columns: [columnOne]
    };
    playersAndNumbers.push(gameColumns);
    return playersAndNumbers;
  }

  private getPlayers(): Content[] {
    const playersAndNumbers: Content[] = [];
    playersAndNumbers.push(this.pdfUtilityService.generateSectionTitle('Players'));

    const columnOne = [
      this.pdfUtilityService.generateLabelValueRow('Played: ', `${this.comprehensiveReport.UserCounts.NumberOfActiveUsers}`),
      this.pdfUtilityService.generateLabelValueRow('Not Played: ', `${this.comprehensiveReport.UserCounts.NumberOfInactiveUsers}`)
    ];

    const gameColumns: Content = {
      columns: [columnOne]
    };
    playersAndNumbers.push(gameColumns);
    return playersAndNumbers;
  }

  private getFinancialDetails(): Content[] {
    const financialDetails: Content[] = [];
    financialDetails.push(this.pdfUtilityService.generateSectionTitle('Financial Details'));
    const columnOne = [
      this.pdfUtilityService.generateLabelValueRow('Gross Sales: ', `${this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.GrossTotalSales)}`),
      this.pdfUtilityService.generateLabelValueRow('Charity Host Admin Fees: ', `(-${this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.AdministrativeCommissionTotal)})`),
      this.pdfUtilityService.generateLabelValueRow('Causable Fees: ', `(-${this.currencyPipe.transform(this.comprehensiveReport.FinancialTotals.CausableFees)})`),
    ];

    // Add tax breakdown based on what's available
    const ft = this.comprehensiveReport.FinancialTotals;
    if (ft.GstTotal && ft.GstTotal > 0) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('GST on Causable Fees: ', `(-${this.currencyPipe.transform(ft.GstTotal)})`));
    }
    if (ft.PstTotal && ft.PstTotal > 0) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow(`${this.pstLabel()} on Causable Fees: `, `(-${this.currencyPipe.transform(ft.PstTotal)})`));
    }
    if (ft.HstTotal && ft.HstTotal > 0) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('HST on Causable Fees: ', `(-${this.currencyPipe.transform(ft.HstTotal)})`));
    }
    // Fallback for existing data without tax breakdown
    if (!ft.GstTotal && !ft.HstTotal && ft.CausableFeesTax > 0) {
      columnOne.push(this.pdfUtilityService.generateLabelValueRow('HST on Causable Fees: ', `(-${this.currencyPipe.transform(ft.CausableFeesTax)})`));
    }

    columnOne.push(
      this.pdfUtilityService.generateLabelValueRow('Stripe Fees: ', `(-${this.currencyPipe.transform(ft.StripeFees)})`),
      this.pdfUtilityService.generateLabelValueRow('Refund Fees:', `(-${this.currencyPipe.transform(ft.CharityRefundContributionTotal)})`),
      this.pdfUtilityService.generateLabelValueRow('Prize Amount: ', `(-${this.currencyPipe.transform(ft.NetPotContributionTotal)})`),
      this.pdfUtilityService.generateLabelValueRow('Net Proceeds For Charity: ', `${this.currencyPipe.transform(ft.NetCharityContribution)}`)
    );

    const gameColumns: Content = {
      columns: [columnOne]
    };
    financialDetails.push(gameColumns);
    return financialDetails;
  }

  private getGameCounts(): Content[] {
    const gameCounts: Content[] = [];
    gameCounts.push(this.pdfUtilityService.generateSectionTitle('Game Counts'));
    const columnOne = [
      this.pdfUtilityService.generateLabelValueRow('Total number of Draws: ', `${this.comprehensiveReport.GameWinnerData.TotalNumberOfDraws}`),
      this.pdfUtilityService.generateLabelValueRow('Draws with no Winner: ', `${this.comprehensiveReport.GameWinnerData.NumberOfDrawsWithNoWinners}`)
    ];
    const columnTwo = [
      this.pdfUtilityService.generateLabelValueRow('Draws with a winner: ', `${this.comprehensiveReport.GameWinnerData.NumberOfDrawsWithWinners}`)
    ];

    gameCounts.push({
      columns: [columnOne, columnTwo]
    });

    return gameCounts;
  }
}
